import Lottie from 'lottie-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import kycAnimation from '../../assets/kycAnimation.json';
import { PAGES } from '../../constants/pages';
import useApplicationUid from '../../hooks/useApplicationUid';
import useStepPost from '../../hooks/useStepPost';
import {
  completeApplication,
  upgradeApplication,
} from '../../store/actions/application/applicationActions';
import {
  selectApplication,
  // selectApplicationType,
} from '../../store/selectors/applicationSelectors';
import {
  APPLICATION_TYPE,
  BASIC_ACCOUNT_UPGRADE_TYPES,
  CorporateAccountType,
} from '../../util/constants';
import { APPLICATION_COMPLETE } from '../../util/gtmEvents';
import useGtmHook from '../../util/hooks/useGtmHook';
import ErrorModal from '../../components/Modals/ErrorModal/ErrorModal';
import Paragraph from '../../components/Paragraph/Paragraph';
import { logoutUser } from '../../store/actions/user/userActions';
import themeColors from '../../assets/styles/themeColors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE,
  APPLICATION_TYPE_STORAGE,
} from '../../constants/sessionStorage';
import { ACCOUNT_TYPE_CORPORATE_APP } from '../../constants/accountTypes';

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;
const AnimationContainer = styled.div`
  width: 175px;
  padding: 25px;
`;

const KycProcessing = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { gtmEvent } = useGtmHook();
  const { ApplicationUid } = useApplicationUid();
  const Application = useSelector(selectApplication);
  const AccountUid = Application?.AccountUid;
  const { ApplicationType, AccountUpgradeType } = Application;
  const { onCompleteAppStep } = useStepPost({ nextPath: '' });
  const [error, setError] = useState();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState();
  const { BrokerName } = useSelector(selectSettings);
  const AccountType = retrieveFromSessionStorage(ACCOUNT_TYPE);
  const ApplicationTypeStorage = retrieveFromSessionStorage(
    APPLICATION_TYPE_STORAGE,
  );

  const isBusinessType =
    ApplicationTypeStorage === ACCOUNT_TYPE_CORPORATE_APP &&
    [
      CorporateAccountType[1],
      CorporateAccountType[2],
      CorporateAccountType[3],
      CorporateAccountType[4],
      CorporateAccountType[5],
    ].includes(AccountType);

  useEffect(() => {
    if (Application?.ApplicationStep === '/kyc/processing') {
      document.addEventListener('keydown', (e) => {
        if ((e.which || e.keyCode) === 116 || (e.which || e.keyCode) === 82) {
          e.preventDefault();
        }
      });
    }
  });

  useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, []);
  const alertUser = (e) => {
    e.preventDefault();
    e.returnValue = '';
  };
  const isUpgrade =
    AccountUpgradeType === BASIC_ACCOUNT_UPGRADE_TYPES.IN_PROCESS;

  useEffect(() => {
    const onSuccess = ({ IsSuccess, AccountNumber }) => {
      gtmEvent(APPLICATION_COMPLETE, {
        event_label: 'registration',
        account_number: AccountNumber,
        application_type: APPLICATION_TYPE[ApplicationType],
      });

      if (APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE[3]) {
        onCompleteAppStep(PAGES.KYC_SUCCESS);
        return;
      }

      if (IsSuccess === true) {
        if (isBusinessType) {
          onCompleteAppStep(PAGES.KYC_VERIFICATION);
          return;
        }

        onCompleteAppStep(PAGES.KYC_SUCCESS);
      }

      if (IsSuccess === false) {
        onCompleteAppStep(PAGES.KYC_VERIFICATION);
      }
    };

    const onError = (errorMsg) => {
      setError(errorMsg);
      setIsErrorModalOpen(true);
    };

    dispatch(
      isUpgrade
        ? upgradeApplication({
            ApplicationUid,
            onSuccess,
            onError,
          })
        : completeApplication({
            ApplicationUid,
            onSuccess,
            onError,
          }),
    );
  }, [ApplicationUid, dispatch, history]); // eslint-disable-line

  const onRedirection = () => {
    if (AccountUid) {
      dispatch(logoutUser());
      return;
    }
    history.push(PAGES.AGREEMENT);
  };

  const onClose = () => {
    setIsErrorModalOpen(false);
    if (AccountUid) {
      dispatch(logoutUser());
      return;
    }
    history.push(PAGES.AGREEMENT);
  };

  return (
    <InnerContainer>
      <Helmet>
        <title>
          {t('kycProcessing.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <AnimationContainer>
        <Lottie animationData={kycAnimation} />
      </AnimationContainer>
      <Paragraph color={themeColors.colorTextPrimary} variant="regular">
        {t('kycProcessing.review')}
      </Paragraph>
      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => onClose()}
        errorMessage={error}
        redirect={onRedirection}
      />
    </InnerContainer>
  );
};

export default KycProcessing;
